import ColorArticleBanner from "./ColorArticleBanner.jsx";
import FaqAnswer from "./FaqAnswer.jsx";
import { clientFormatName } from "../../utils/bookFormatLabel.js";
import { useEffect, useState, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Box from "@mui/joy/Box";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import IconButton from "@mui/joy/IconButton";
import Chip from "@mui/joy/Chip";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

import CircularProgress from "@mui/joy/CircularProgress";
import { Grid, Modal, ModalDialog, ModalOverflow, Sheet } from "@mui/joy";
import { bookApi } from "../../api.js";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, FreeMode, Mousewheel } from 'swiper/modules';



// ─── Иконка-заглушка если нет превью ─────────────────────────────────────────

function BookShapeIcon({ widthMm, heightMm, color = "currentColor" }) {
  const w = widthMm || 1;
  const h = heightMm || 1;
  const ar = w / h;
  const bw = Math.round(24 + ar * 10);
  const bh = Math.round(34 / ar < 46 ? 34 / ar : 46);
  return (
    <svg viewBox={`0 0 48 ${bh + 4}`} width="36" height="36" fill="none">
      <rect x="2" y="2" width={bw} height={bh} rx="3"
        fill={color} opacity="0.13" stroke={color} strokeWidth="1.5"/>
      <rect x="6" y="7" width={bw - 8} height="2" rx="1" fill={color} opacity="0.3"/>
      <rect x="6" y="12" width={bw - 14} height="2" rx="1" fill={color} opacity="0.2"/>
      <rect x="6" y="17" width={bw - 10} height="2" rx="1" fill={color} opacity="0.2"/>
    </svg>
  );
}

// ─── Аккордеон FAQ ────────────────────────────────────────────────────────────

function FAQItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <Box
      sx={{
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.8,
          px: 0,
          cursor: "pointer",
          gap: 1.5,
          userSelect: "none",
          "&:hover .faq-q": { color: "primary.500" },
        }}
      >
        <Typography
          className="faq-q"
          level="body-sm"
          sx={{ fontWeight: 600, transition: "color .15s", lineHeight: 1.4, flex: 1 }}
        >
          {item.question}
        </Typography>
        <motion.div
          animate={{ rotate: open ? 135 : 0 }}
          transition={{ duration: 0.2 }}
          style={{ flexShrink: 0, display: "flex" }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M9 3.5V14.5M3.5 9H14.5" stroke="currentColor"
              strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </motion.div>
      </Box>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="faq-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <FaqAnswer answer={item.answer} answerHtml={item.answer_html} />
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}

// ─── Тултип вариантов страниц ─────────────────────────────────────────────────

function PageVariantTooltip({ variants, onSelect, anchorRef }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (anchorRef?.current) {
      const r = anchorRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 8, left: r.left });
    }
  }, [anchorRef]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.94, y: -6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.94, y: -6 }}
      transition={{ duration: 0.16 }}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        zIndex: 99999,
        minWidth: 200,
      }}
    >
      <Box
        sx={{
          bgcolor: "background.surface",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "14px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.14)",
          overflow: "hidden",
          p: 0.5,
        }}
      >
        <Typography
          level="body-xs"
          sx={{ px: 1.5, pt: 1, pb: 0.5, color: "text.tertiary", fontWeight: 600,
            textTransform: "uppercase", letterSpacing: "0.07em", fontSize: "10px" }}
        >
          Выберите объём
        </Typography>
        {variants.map(v => (
          <Box
            key={v.id}
            onClick={() => onSelect(v)}
            sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              px: 1.5, py: 1, borderRadius: "10px", cursor: "pointer",
              transition: "background .15s",
              "&:hover": { bgcolor: "color-mix(in srgb, var(--joy-palette-primary-500) 8%, transparent)" },
            }}
          >
            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
              {v.pages} страниц
            </Typography>
            <Chip size="sm" variant="soft" color="primary" sx={{ fontWeight: 700 }}>
              {Number(v.price).toLocaleString("ru-RU")} ₽
            </Chip>
          </Box>
        ))}
      </Box>
    </motion.div>
  );
}

// Первое превью — карточка, остальные — галерея внутри формата.
function getFormatPreviewGroups(fmt) {
  const legacy = fmt.previews ?? fmt.preview_files ?? fmt.media ?? [];
  const ordered = Array.isArray(legacy) ? [...legacy].sort((a, b) => {
    const order = (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0);
    if (order) return order;
    const aId = String(a.id ?? "");
    const bId = String(b.id ?? "");
    return aId < bId ? -1 : aId > bId ? 1 : 0;
  }) : [];

  return {
    cardPreview: Object.prototype.hasOwnProperty.call(fmt, "card_preview")
      ? fmt.card_preview
      : ordered[0] ?? null,
    detailPreviews: Array.isArray(fmt.detail_previews)
      ? fmt.detail_previews
      : ordered.slice(1),
  };
}

// ─── Карточка формата (сетка) ─────────────────────────────────────────────────

function formatDisplayName(fmt) {
  return clientFormatName(fmt);
}

function FormatCard({ fmt, onDetail }) {
  const { cardPreview: preview } = getFormatPreviewGroups(fmt);
  const prices = (fmt.page_variants || []).map(v => Number(v.price)).filter(Number.isFinite);
  const minPrice = prices.length ? Math.min(...prices) : null;
  return <Box sx={{ border: "1.5px solid", borderColor: "divider", borderRadius: 16,
    overflow: "hidden", bgcolor: "background.surface", display: "flex",
    flexDirection: "row", minWidth: 0 }}>
    <Box sx={{ width: "38%", flexShrink: 0,
      height: { xs: "clamp(150px, 25dvh, 205px)", sm: 220 }, position: "relative",
      display: "grid", placeItems: "center", bgcolor: "background.level1", overflow: "hidden" }}>
      {preview ? preview.media_type === "video"
        ? <Box component="video" src={preview.file_url} autoPlay muted playsInline loop
            sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
        : <Box component="img" src={preview.file_url} alt={formatDisplayName(fmt)}
            decoding="async" sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
        : <BookShapeIcon widthMm={fmt.display_width_mm || fmt.width_mm} heightMm={fmt.height_mm} color="#834A90" />}
    </Box>
    <Box sx={{ p: { xs: 1.25, sm: 1.5 }, flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: .75 }}>
      <Typography level="title-lg" sx={{
        fontWeight: 800,
        fontSize: { xs: "clamp(11px, 3.5vw, 16px)", sm: "16px" },
        lineHeight: 1.25,
        letterSpacing: "-0.2px",
        whiteSpace: "nowrap",
      }}>{formatDisplayName(fmt)}</Typography>
      <Typography level="body-xs">{fmt.display_width_mm || fmt.width_mm} × {fmt.height_mm} мм</Typography>
      <Stack direction="row" gap={.5} flexWrap="wrap">
        {(fmt.page_variants || []).map(v => <Chip key={v.id} size="sm" sx={{ fontSize: 10 }}>{v.pages} стр.</Chip>)}
      </Stack>
      {minPrice !== null && <Typography level="body-sm" sx={{ fontWeight: 700 }}>от {minPrice.toLocaleString("ru-RU")} ₽</Typography>}
      <Button size="sm" onClick={() => onDetail(fmt)} sx={{ mt: "auto", borderRadius: 10, minHeight: 36 }}>Открыть</Button>
    </Box>
  </Box>;
}

// ─── Слайдер фото ────────────────────────────────────────────────────────────

function PhotoSlider({ previews = [] }) {
  const [current, setCurrent] = useState(0);
  const swipe = useRef(null);
  // При смене формата начинаем с первого изображения.
  const galleryKey = previews.map(p => p.id || p.file_url).join("|");
  useEffect(() => { setCurrent(0); }, [galleryKey]);
  const index = Math.min(current, Math.max(0, previews.length - 1));
  const active = previews[index];
  const goTo = n => setCurrent(Math.max(0, Math.min(previews.length - 1, n)));
  if (!active) return null;
  const video = active.media_type === "video";
  return (
    <Box data-format-gallery sx={{ width: "100%", minWidth: 0 }}>
      <Box tabIndex={0} role="region" aria-label="Фотографии формата книги"
        onKeyDown={e => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "ArrowLeft") { e.preventDefault(); goTo(index - 1); }
          if (e.key === "ArrowRight") { e.preventDefault(); goTo(index + 1); }
        }}
        onPointerDown={e => {
          if (video || e.button !== 0 || e.target.closest("button")) return;
          swipe.current = { x: e.clientX, y: e.clientY };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerUp={e => {
          const start = swipe.current; swipe.current = null;
          if (!start) return;
          const dx = e.clientX - start.x, dy = e.clientY - start.y;
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) goTo(index + (dx < 0 ? 1 : -1));
        }}
        onPointerCancel={() => { swipe.current = null; }}
        sx={{ position: "relative", width: "100%", aspectRatio: "4 / 5",
          maxHeight: { xs: "min(420px, 55dvh)", md: 480 }, overflow: "hidden",
          borderRadius: 14, bgcolor: "background.level1", touchAction: video ? "auto" : "pan-y",
          "&:focus-visible": { outline: "2px solid", outlineColor: "primary.500", outlineOffset: 2 } }}>
        {video ? <Box key={active.file_url} component="video" src={active.file_url}
          controls autoPlay muted loop playsInline preload="metadata"
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
          : <Box key={active.file_url} component="img" src={active.file_url}
            alt={`Фото формата ${index + 1}`} draggable={false} decoding="async"
            sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", userSelect: "none" }} />}
        {previews.length > 1 && <>
          <IconButton aria-label="Предыдущее фото" disabled={index === 0} onClick={() => goTo(index - 1)}
            sx={{ position: "absolute", left: 8, top: "45%", borderRadius: "50%", bgcolor: "rgba(255,255,255,.9)" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m15 5-7 7 7 7" stroke="currentColor" strokeWidth="2" /></svg>
          </IconButton>
          <IconButton aria-label="Следующее фото" disabled={index === previews.length - 1} onClick={() => goTo(index + 1)}
            sx={{ position: "absolute", right: 8, top: "45%", borderRadius: "50%", bgcolor: "rgba(255,255,255,.9)" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m9 5 7 7-7 7" stroke="currentColor" strokeWidth="2" /></svg>
          </IconButton>
          <Box role="status" sx={{ position: "absolute", top: 10, right: 10, px: 1, py: .25,
            borderRadius: 20, bgcolor: "rgba(0,0,0,.55)", color: "#fff", fontSize: 12 }}>{index + 1} / {previews.length}</Box>
        </>}
      </Box>
      {previews.length > 1 && <Box sx={{ display: "flex", gap: 1, overflowX: "auto", mt: 1, p: .5 }}>
        {previews.map((p, i) => <Box component="button" type="button" key={p.id || `${p.file_url}-${i}`}
          onClick={() => goTo(i)} aria-label={`Показать фото ${i + 1}`} aria-pressed={i === index}
          sx={{ flex: "0 0 56px", height: 64, p: .25, borderRadius: 8, overflow: "hidden", cursor: "pointer",
            border: "2px solid", borderColor: i === index ? "primary.500" : "divider", bgcolor: "background.level1" }}>
          {p.media_type === "video" ? <span style={{ fontSize: 12 }}>Видео</span>
            : <img src={p.file_url} alt="" loading="lazy" draggable={false}
                style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />}
        </Box>)}
      </Box>}
    </Box>
  );
}

// ─── Карточка скидки ─────────────────────────────────────────────────────────

function DiscountCard({ discount }) {
  const conditionText = (() => {
    if (!discount.condition_value || discount.condition_value === 0) {
      return "Для всех заказов";
    }
    if (discount.condition === "min_orders") {
      const n = discount.condition_value;
      const word = n === 1 ? "заказа" : n < 5 ? "заказов" : "заказов";
      return `От ${n} ${word} на аккаунте`;
    }
    if (discount.condition === "min_items") {
      const n = discount.condition_value;
      const word = n === 1 ? "книги" : n < 5 ? "книг" : "книг";
      return `От ${n} ${word} в заказе`;
    }
    return "";
  })();

  const percentNum = parseFloat(discount.percent);

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "14px",
        border: "1.5px solid",
        borderColor: "color-mix(in srgb, var(--joy-palette-success-400) 35%, transparent)",
        background: "linear-gradient(135deg, rgba(34,197,94,0.04) 0%, rgba(16,185,129,0.06) 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Бейдж процента */}
      <Box sx={{
        position: "absolute", top: 0, right: 0,
        bgcolor: "success.500",
        color: "white",
        px: 1.5, py: 0.4,
        borderBottomLeftRadius: "12px",
        fontSize: "12px",
        fontWeight: 800,
        letterSpacing: "0.02em",
      }}>
        −{percentNum}%
      </Box>

      {/* Название */}
      <Typography level="body-sm" sx={{ fontWeight: 700, color: "text.primary", pr: 6, mb: 0.5 }}>
        {discount.title}
      </Typography>

      {/* Описание */}
      {discount.description && (
        <Typography level="body-xs" sx={{ color: "text.secondary", mb: 0.75, lineHeight: 1.5 }}>
          {discount.description}
        </Typography>
      )}

      {/* Условие */}
      <Stack direction="row" alignItems="center" gap={0.75}>
        <Box sx={{
          width: 18, height: 18, borderRadius: "50%",
          bgcolor: "color-mix(in srgb, var(--joy-palette-success-500) 12%, transparent)",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2 5.5L4 7.5L8 3" stroke="var(--joy-palette-success-600)"
              strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Box>
        <Typography level="body-xs" sx={{ color: "success.700", fontWeight: 600 }}>
          {conditionText}
        </Typography>
      </Stack>

      {/* Привязка к формату */}
      {discount.book_format_title && (
        <Stack direction="row" alignItems="center" gap={0.75} sx={{ mt: 0.5 }}>
          <Box sx={{
            width: 18, height: 18, borderRadius: "50%",
            bgcolor: "color-mix(in srgb, var(--joy-palette-primary-500) 12%, transparent)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <rect x="1.5" y="2" width="7" height="6" rx="1" stroke="var(--joy-palette-primary-500)"
                strokeWidth="1.2" fill="none"/>
            </svg>
          </Box>
          <Typography level="body-xs" sx={{ color: "primary.600", fontWeight: 600 }}>
            Для формата: {discount.book_format_title}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

// ─── Слайдер отзывов ─────────────────────────────────────────────────────────

function ReviewsSlider({ reviews }) {
  const count = reviews.length;
  const [openReview, setOpenReview] = useState(null);

  if (count === 0) return null;

  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography level="body-xs" sx={{
        fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.08em", color: "text.tertiary",
        mb: 1.5, fontSize: "10px",
      }}>
        Отзывы
      </Typography>

      <Swiper
        modules={[Navigation, FreeMode, Mousewheel]}
        spaceBetween={12}
        slidesPerView={"auto"}
        freeMode={{ enabled: true, sticky: false }}
        mousewheel={{ forceToAxis: true }}
        navigation
        grabCursor
        style={{ paddingBottom: 4 }}
      >
        {reviews.map((review, i) => (
          <SwiperSlide key={i} style={{ width: "auto" }}>
            <Box
              onClick={() => setOpenReview(review)}
              sx={{
                width: { xs: 200, sm: 220 },
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                userSelect: "none",
                cursor: "pointer",
                transition: "transform .2s, box-shadow .2s",
                borderRadius: "14px",
                "&:hover": {
                  transform: "translateY(-2px)",
                },
              }}
            >
              {/* Фото книги */}
              <Box sx={{
                width: "100%",
                aspectRatio: "3 / 4",
                borderRadius: "12px",
                overflow: "hidden",
                bgcolor: "background.level1",
              }}>
                <Box
                  component="img"
                  src={review.image_url}
                  alt={review.author_name || "Отзыв"}
                  draggable={false}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    pointerEvents: "none",
                  }}
                />
              </Box>

              {/* Текст под фото */}
              <Box sx={{ px: 0.5 }}>
                {review.author_name && (
                  <Typography sx={{ fontWeight: 700, fontSize: "14px", lineHeight: 1.3, mb: 0.5 }}>
                    {review.author_name}
                  </Typography>
                )}
                {review.text && (
                  <Typography sx={{
                    fontSize: "12px", lineHeight: 1.5,
                    color: "text.secondary",
                    display: "-webkit-box",
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}>
                    «{review.text}»
                  </Typography>
                )}
                <Button variant="soft" color="neutral" sx={{ mt: 1, width: '100%' }}>
                  Читать
                </Button>
              </Box>
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* ── Модалка развёрнутого отзыва ── */}
      <AnimatePresence>
        {openReview && (
          <Modal
            open={!!openReview}
            onClose={() => setOpenReview(null)}
            sx={{ zIndex: 10020 }}
            slotProps={{
              backdrop: {
                component: motion.div,
                initial: { opacity: 0, backdropFilter: "blur(0px)" },
                animate: { opacity: 1, backdropFilter: "blur(6px)" },
                exit: { opacity: 0, backdropFilter: "blur(0px)" },
                transition: { duration: 0.25 },
              },
            }}
          >
            <ModalOverflow>
              <ModalDialog
                component={motion.div}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 30, scale: 0.95 }}
                transition={{ duration: 0.25, ease: [0.32, 0, 0.08, 1] }}
                sx={{
                  maxWidth: 420,
                  width: "90vw",
                  p: 0,
                  borderRadius: "20px",
                  overflow: "hidden",
                  boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
                }}
              >
                {/* Картинка */}
                <Box sx={{
                  width: "100%",
                  // maxHeight: 340,
                  overflow: "hidden",
                  bgcolor: "background.level1",
                  position: "relative",
                }}>
                  <Box
                    component="img"
                    src={openReview.image_url}
                    alt={openReview.author_name || "Отзыв"}
                    sx={{
                      width: "100%",
                      // height: 340,
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  {/* Кнопка закрыть */}
                  <IconButton
                    size="sm"
                    onClick={() => setOpenReview(null)}
                    sx={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      bgcolor: "rgba(0,0,0,0.45)",
                      backdropFilter: "blur(4px)",
                      color: "white",
                      borderRadius: "50%",
                      width: 32, height: 32,
                      "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
                    }}
                  >
                    <CloseRoundedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>

                {/* Контент */}
                <Box sx={{ p: 2.5, maxHeight: "40vh", overflowY: "auto" }}>
                  {openReview.author_name && (
                    <Typography sx={{ fontWeight: 800, fontSize: "17px", lineHeight: 1.3, mb: 0.5 }}>
                      {openReview.author_name}
                    </Typography>
                  )}
                  {openReview.author_city && (
                    <Typography sx={{ fontSize: "13px", color: "text.tertiary", mb: 1.5 }}>
                      {openReview.author_city}
                    </Typography>
                  )}
                  {openReview.text && (
                    <Typography sx={{
                      fontSize: "14px",
                      lineHeight: 1.7,
                      color: "text.secondary",
                      whiteSpace: "pre-line",
                    }}>
                      «{openReview.text}»
                    </Typography>
                  )}
                </Box>
              </ModalDialog>
            </ModalOverflow>
          </Modal>
        )}
      </AnimatePresence>
    </Box>
  );
}


// ─── Слайд детальной информации о формате ────────────────────────────────────

function DetailSlide({ fmt, onBack, onSelect, onClose }) {
  const [activeVariant, setActiveVariant] = useState(fmt.page_variants?.[0] ?? null);
  const btnRef = useRef(null);
  const [showVariants, setShowVariants] = useState(false);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const scrollRef = useRef(null);
  const { detailPreviews: previews } = getFormatPreviewGroups(fmt);

  return (
    <Box
      component={motion.div}
      key="detail"
      initial={{ x: "100%", opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: "100%", opacity: 0 }}
      transition={{ duration: 0.28, ease: [0.32, 0, 0.08, 1] }}
      sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}
    >
      {/* Заголовок с кнопкой назад */}
      <Stack direction={'row'}
        sx={{
          display: "flex", alignItems: "center", justifyContent: 'space-between',
          px: { xs: 2, sm: 3 }, pt: { xs: 1, sm: 1 }, pb: 1,
          borderBottom: "1px solid", borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Stack direction={'row'} sx={{ alignItems: "center", gap: 1 }}>
          {onBack && (
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              onClick={onBack}
              sx={{ borderRadius: "10px" }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M11 14L6 9L11 4" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </IconButton>
          )}
          <Typography level="title-md" sx={{ fontWeight: 700 }}>
            {formatDisplayName(fmt)}
          </Typography>
        </Stack>
        {onClose && (
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={onClose}
            sx={{
              zIndex: 10,
              borderRadius: "10px",
              bgcolor: "rgba(0,0,0,0.05)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.10)" },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Stack>

        {/* Прокручиваемое содержимое */}
        <Stack
          ref={scrollRef}
          direction={{ xs: 'column', md: 'row' }}
          sx={{ flex: 1, minHeight: 0, overflowY: "auto", gap: { xs: 2, md: 3 },
            alignItems: "flex-start", px: { xs: 2, sm: 3 }, py: 2,
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": { borderRadius: "4px",
              bgcolor: "color-mix(in srgb, var(--joy-palette-neutral-500) 20%, transparent)" },
          }}
        >
          {/* Слайдер превью */}
          {previews.length > 0 && (
            <Box sx={{ width: { xs: "100%", md: "40%" }, flex: { md: "0 0 40%" }, minWidth: 0 }}>
              <PhotoSlider previews={previews} />
            </Box>
          )}

          <Box sx={{ minWidth: 0, width: { xs: "100%", md: "auto" }, flex: { md: "1 1 0%" },
            height: "fit-content", display: "flex", flexDirection: "column", overflowWrap: "anywhere" }}>
            {/* Название + размеры + описание */}
            <Typography level="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
              {formatDisplayName(fmt)}
            </Typography>
            {/* <Chip size="sm" variant="soft" color={'primary'} sx={{ fontFamily: "monospace", mb: 1 }}>
              {fmt.display_width_mm || fmt.width_mm} × {fmt.height_mm} мм
            </Chip> */}
            {fmt.description && (
              <Box
                className="format-description"
                sx={{
                  '& p': { margin: '0 0 8px 0', lineHeight: 1.5 },
                  '& strong, & b': { fontWeight: 700, color: 'primary.500' },
                  '& em, & i': { fontStyle: 'italic' },
                  '& ul, & ol': { paddingLeft: '20px', margin: '8px 0' },
                  '& li': { marginBottom: '4px' },
                  '& mark': { 
                    backgroundColor: '#fff3cd', 
                    padding: '2px 4px', 
                    borderRadius: '4px' 
                  },
                  '& a': { color: 'primary.500', textDecoration: 'underline' },
                  '& h3, & h4': { fontWeight: 700, margin: '12px 0 6px 0' },

                  color: "text.secondary", fontSize: 15, mb: 2.5, lineHeight: 1.2, whiteSpace: "pre-line"
                }}
                dangerouslySetInnerHTML={{ __html: fmt.description || '' }}
              />
            )}

            {/* Варианты страниц */}
            {fmt.page_variants?.length > 0 && (
              <Box sx={{ mb: 2.5 }}>
                <Typography level="body-xs" sx={{ fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.08em", color: "text.tertiary", mb: 1.5, fontSize: "10px" }}>
                  Объём и стоимость
                </Typography>
                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1,
                }}>
                  {fmt.page_variants.map(v => (
                    <Box
                      key={v.id}
                      onClick={() => setActiveVariant(v)}
                      sx={{
                        px: 2, py: 1.25, borderRadius: "12px", cursor: "pointer",
                        border: "1.5px solid",
                        borderColor: activeVariant?.id === v.id
                          ? "primary.400"
                          : "divider",
                        bgcolor: activeVariant?.id === v.id
                          ? "color-mix(in srgb, var(--joy-palette-primary-500) 8%, transparent)"
                          : "background.level1",
                        transition: "all .15s",
                        userSelect: "none",
                        "&:hover": {
                          borderColor: activeVariant?.id === v.id
                            ? "primary.400"
                            : "color-mix(in srgb, var(--joy-palette-primary-400) 50%, transparent)",
                        },
                      }}
                    >
                      <Typography level="body-sm" sx={{ fontWeight: 700,
                        color: activeVariant?.id === v.id ? "primary.600" : "text.primary" }}>
                        {v.pages} страниц
                      </Typography>
                      <Typography level="body-xs" sx={{ color: "text.secondary" }}>
                        {Number(v.price).toLocaleString("ru-RU")} ₽
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* FAQ */}
            {fmt.faqs?.length > 0 && (
              <Box>
                <Typography level="body-xs" sx={{ fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.08em", color: "text.tertiary", mb: 1, fontSize: "10px" }}>
                  Частые вопросы
                </Typography>
                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: "14px",
                  overflow: "hidden", px: 2 }}>
                  {fmt.faqs.map((item, i) => (
                    <FAQItem key={item.id} item={item} index={i} />
                  ))}
                </Box>
              </Box>
            )}
         
            {/* Статья о цветопередаче — после FAQ, перед отзывами */}
            <ColorArticleBanner />

            {/* Отзывы */}
            {fmt.reviews?.length > 0 && (
              <ReviewsSlider reviews={fmt.reviews} />
            )}

            {/* Скидки */}
            {fmt.discounts?.length > 0 && (
              <Box sx={{ mt: 2.5 }}>
                <Typography level="body-xs" sx={{
                  fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.08em", color: "text.tertiary",
                  mb: 1.5, fontSize: "10px",
                }}>
                  Скидки и акции
                </Typography>
                <Stack gap={1}>
                  {fmt.discounts.map(d => (
                    <DiscountCard key={d.id} discount={d} />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </Stack>


        {/* Приклеенная кнопка внизу */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 1,
            borderTop: "1px solid",
            borderColor: "divider",
            // bgcolor: "background.surface",
            flexShrink: 0,
            position: "relative",
          }}
        >
          {activeVariant && (
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                {activeVariant.pages} страниц
              </Typography>
              <Typography level="body-lg" sx={{ fontWeight: 800, color: "primary.600" }}>
                {Number(activeVariant.price).toLocaleString("ru-RU")} ₽
              </Typography>
            </Stack>
          )}

          <Box sx={{ position: "relative" }}>
            <Button
              ref={btnRef}
              variant="solid"
              color="primary"
              fullWidth
              size="lg"
              disabled={!activeVariant}
              onClick={() => activeVariant && onSelect(fmt, activeVariant)}
              sx={{ fontWeight: 700, borderRadius: "14px" }}
            >
              {activeVariant
                ? `Создать книгу · ${Number(activeVariant.price).toLocaleString("ru-RU")} ₽`
                : "Выберите объём"}
            </Button>

            <AnimatePresence>
              {showVariants && fmt.page_variants?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 8 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 8 }}
                  transition={{ duration: 0.16 }}
                  style={{
                    position: "absolute",
                    bottom: "calc(100% + 8px)",
                    left: 0,
                    right: 0,
                    zIndex: 10,
                  }}
                >
                  <Box sx={{ bgcolor: "background.surface", border: "1px solid",
                    borderColor: "divider", borderRadius: "14px",
                    boxShadow: "0 -8px 32px rgba(0,0,0,0.12)", overflow: "hidden", p: 0.5 }}>
                    {fmt.page_variants.map(v => (
                      <Box
                        key={v.id}
                        onClick={() => { setShowVariants(false); setActiveVariant(v); onSelect(fmt, v); }}
                        sx={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          px: 1.5, py: 1.2, borderRadius: "10px", cursor: "pointer",
                          transition: "background .15s",
                          "&:hover": { bgcolor: "color-mix(in srgb, var(--joy-palette-primary-500) 8%, transparent)" },
                        }}
                      >
                        <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                          {v.pages} страниц
                        </Typography>
                        <Chip size="sm" variant="soft" color="primary" sx={{ fontWeight: 700 }}>
                          {Number(v.price).toLocaleString("ru-RU")} ₽
                        </Chip>
                      </Box>
                    ))}
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </Box>
    </Box>
  );
}

// ─── Список форматов ──────────────────────────────────────────────────────────

function ListSlide({ formats, onDetail, onSelect, onClose }) {
  return (
    <motion.div
      key="list"
      initial={{ x: 0, opacity: 1 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: "-100%", opacity: 0 }}
      transition={{ duration: 0.28, ease: [0.32, 0, 0.08, 1] }}
      style={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      {/* Заголовок */}
      <Stack direction={'row'} sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 1, sm: 3 }, justifyContent: 'space-between', alignItems: 'flex-start',pb: 2, flexShrink: 0 }}>
        <Stack direction={'column'}>
          <Typography
            level="h3"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "20px", sm: "24px" },
              letterSpacing: "-0.02em",
              mb: 0.5,
            }}
          >
            Выберите формат
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary" }}>
            Определяет размер и пропорции вашей книги
          </Typography>
        </Stack>
        {onClose && (
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={onClose}
            sx={{
              zIndex: 10,
              borderRadius: "10px",
              bgcolor: "rgba(0,0,0,0.05)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.10)" },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Stack>

      {/* Сетка карточек */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: 2, sm: 3 },
          pb: 3,
          pt: 1,
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-thumb": { borderRadius: "4px",
            bgcolor: "color-mix(in srgb, var(--joy-palette-neutral-500) 20%, transparent)" },
        }}
      >
        {formats.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: 200 }}>
            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
              Форматы не найдены
            </Typography>
          </Stack>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr)",
              gap: 1.5,
            }}
          >
            {formats.map(fmt => (
              <FormatCard
                key={fmt.id}
                fmt={fmt}
                onDetail={onDetail}
                onSelect={onSelect}
              />
            ))}
          </Box>
        )}
      </Box>
    </motion.div>
  );
}



// ─── Основной компонент ───────────────────────────────────────────────────────

export default function ModalBookSize({ open, onConfirm, onClose }) {
  const [formats, setFormats] = useState([]);
  // "loading"  — данные ещё не пришли (показываем спиннер поверх модалки)
  // "list"     — показываем список форматов
  // "detail"   — показываем детали конкретного формата
  const [screen, setScreen] = useState("loading");
  const [detailFmt, setDetailFmt] = useState(null);

  useEffect(() => {
    if (!open) return;
    setScreen("loading");
    setDetailFmt(null);
    bookApi.getBookFormats()
      .then(data => {
        console.log("[ModalBookSize] formats from API:", data); // ← добавь
        setFormats(data);
        if (data.length === 1) {
          // Один формат — сразу показываем детали, без промежуточного экрана списка
          setDetailFmt(data[0]);
          setScreen("detail");
        } else {
          setScreen("list");
        }
      })
      .catch(err => {
        console.error("Ошибка загрузки форматов:", err);
        setScreen("list"); // покажем пустой список с сообщением об ошибке
      });
  }, [open]);

  const handleDetail = (fmt) => {
    setDetailFmt(fmt);
    setScreen("detail");
  };

  const handleBack = () => {
    setDetailFmt(null);
    setScreen("list");
  };

  const handleSelect = (fmt, variant) => {
    onConfirm({
      id: fmt.id,
      label: formatDisplayName(fmt),
      widthMm: fmt.width_mm,
      heightMm: fmt.height_mm,
      pages: variant.pages,
      price: variant.price,
    });
  };

  return (
    <AnimatePresence>
      {open && (
        <Modal
          sx={{ zIndex: 10013 }}
          open={open}
          onClose={onClose}
          slotProps={{
            backdrop: {
              component: motion.div,
              initial: { opacity: 0, backdropFilter: 'blur(0px)' },
              animate: { opacity: 1, backdropFilter: 'blur(8px)' },
              exit: { opacity: 0, backdropFilter: 'blur(0px)' },
              transition: { duration: 0.3, ease: "easeInOut" },
            }
          }}
        >
          <ModalOverflow sx={{ height: 'auto', overflow: "hidden", p: 0 }}>
            <ModalDialog
              size="lg"
              component={motion.div}
              initial={{ opacity: 0, y: -10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              sx={(theme) => ({
                position: "relative",
                [theme.breakpoints.up("xl")]: {
                  width: screen === "list" ? '480px' : '100dvw',
                  maxWidth: screen === "list" ? '480px' : '900px',
                },
                [theme.breakpoints.down("xl")]: {
                  width: screen === "list" ? '480px' : '100vw',
                  maxWidth: screen === "list"
                    ? 'min(480px, calc(100vw - 32px))'
                    : '70vw',
                },
                [theme.breakpoints.down("lg")]: {
                  width: screen === "list" ? '480px' : '100vw',
                  maxWidth: screen === "list"
                    ? 'min(480px, calc(100vw - 32px))'
                    : '90vw',
                },
                [theme.breakpoints.down("md")]: {
                  width: screen === "list" ? '480px' : '100vw',
                  maxWidth: screen === "list"
                    ? 'min(480px, calc(100vw - 32px))'
                    : '900px',
                  maxHeight: "100vh !important",
                  overflowY: 'auto',
                  borderRadius: '0',
                },
                [theme.breakpoints.down("md")]: { width: '100vw', maxWidth: '900px', maxHeight: "100vh !important", overflowY: 'auto', borderRadius: '0' },
                [theme.breakpoints.down("sm")]: {
                  width: '100%',
                  maxWidth: '100vw',
                  minHeight: '100dvh',
                  height: '100dvh',
                  overflowY: 'auto',
                  py: 0,
                  px: 0,
                  m: '0 !important',
                  borderRadius: '0',
                  alignSelf: 'flex-end',
                },
                pb: { xs: 0, sm: 2 },
                p: 0,
                maxHeight: "90vh !important",
                width: '100%',
                overscrollBehavior: 'contain',
              })}
            >
              <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
                <AnimatePresence mode="wait">

                  {/* ── Загрузка ── */}
                  {screen === "loading" && (
                    <motion.div
                      key="loading"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      style={{ display: "flex", alignItems: "center", justifyContent: "center",
                        minHeight: 220 }}
                    >
                      <Stack alignItems="center" gap={1.5}>
                        <CircularProgress size="md" />
                        <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                          Загружаем форматы…
                        </Typography>
                      </Stack>
                    </motion.div>
                  )}

                  {/* ── Список форматов ── */}
                  {screen === "list" && (
                    <ListSlide
                      key="list"
                      formats={formats}
                      loading={false}
                      onDetail={handleDetail}
                      onSelect={handleSelect}
                      onClose={onClose}
                    />
                  )}

                  {/* ── Детали формата ── */}
                  {screen === "detail" && detailFmt && (
                    <DetailSlide
                      key="detail"
                      fmt={detailFmt}
                      // Кнопку «назад» показываем только если форматов больше одного
                      onBack={formats.length > 1 ? handleBack : null}
                      onSelect={handleSelect}
                      onClose={onClose}
                    />
                  )}

                </AnimatePresence>
              </Box>
            </ModalDialog>
          </ModalOverflow>
        </Modal>
      )}
    </AnimatePresence>
  );
}